import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { sortBy, merge, find, has } from "lodash-es";

import { PAYMENT_METHODS } from "../helpers/constants";
import { getAllUserAccounts, getUser } from "../api/users";
import useCurrencyFilter from "./useCurrencyFilter";
import useCurrencies from "./useCurrencies";
import _ from "lodash";
import useExtraModules from "./useExtraModules";
import useMainFilter from "./useMainFilter";
import { APP_USER_ID_KEY } from "src/constants/app";
import { CACHE_KEYS } from "src/constants/cache";

export default function useCurrentUser() {
  const queryClient = useQueryClient();
  const { myNetworkIsEnabled, recurringTransactionsIsEnabled } =
    useExtraModules();
  const { getCurrencyDataByCode } = useCurrencies();
  const { mainFilter, setMainFilter } = useMainFilter();

  const currentUserId = localStorage?.getItem?.(APP_USER_ID_KEY) || "";
  const { currencyFilter, setCurrencyFilter } = useCurrencyFilter();

  const user = useQuery({
    queryFn: () => getUser({ _id: currentUserId }),
    queryKey: [CACHE_KEYS.CURRENT_USER],
  });

  const accounts = useQuery({
    queryFn: () => getAllUserAccounts({ _id: currentUserId }),
    queryKey: [CACHE_KEYS.CURRENT_USER_ACCOUNTS],
  });

  const getAccountById = (account: any) =>
    find(accounts?.data?.data, { account });
  const accountExist = (account: any) => !!getAccountById(account);

  const availableCurrencies = useMemo(() => {
    const currencies = new Set(
      (accounts?.data?.data || [])
        .map?.(({ currency }: any) => currency)
        .filter(Boolean),
    );

    return sortBy(
      [...currencies]
        .map((code: any) => getCurrencyDataByCode({ code }))
        .filter(Boolean),
      ["name"],
    );
  }, [accounts?.data?.data, getCurrencyDataByCode]);

  useEffect(() => {
    if (availableCurrencies.length > 0) {
      const availableCurrencyCodes = new Set(
        availableCurrencies.map((item: any) => item?.code).filter(Boolean),
      );

      const firstCurrencyCode = availableCurrencies[0]?.code || "";

      if (
        firstCurrencyCode &&
        (!currencyFilter || !availableCurrencyCodes.has(currencyFilter))
      ) {
        setCurrencyFilter({
          selectedCurrency: firstCurrencyCode,
        });
      }

      return;
    }

    if (currencyFilter) {
      setCurrencyFilter({
        selectedCurrency: "",
      });
    }
  }, [availableCurrencies, currencyFilter, setCurrencyFilter]);

  const resetCache = async () => {
    await Promise.allSettled([
      queryClient.refetchQueries({
        queryKey: [CACHE_KEYS.CURRENT_USER_ACCOUNTS],
      }),
      queryClient.refetchQueries({ queryKey: [CACHE_KEYS.CURRENT_USER] }),
    ]);
  };

  const enabledAccountsList = useMemo(
    () =>
      accounts?.data?.data?.filter(
        (account: any) => !account?.settings?.disabled,
      ) || [],
    [accounts?.data?.data],
  );

  // useEffect(() => {
  //   if (enabledAccountsList?.length === 1) {
  //     const [enabledAccount] = enabledAccountsList;
  //     const { account } = enabledAccount;

  //     if (mainFilter.account !== account) {
  //       setMainFilter({ account });
  //     }
  //   }
  // }, [enabledAccountsList]);

  const defaultColors = useMemo(
    () => ({
      transactions: {
        income: {
          bg: {
            r: 80,
            g: 227,
            b: 194,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        expenses: {
          bg: {
            r: 74,
            g: 144,
            b: 226,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
      },
      months: {
        "1": {
          bg: {
            r: 180,
            g: 43,
            b: 43,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "2": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "3": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "4": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "5": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "6": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "7": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "8": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "9": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "10": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "11": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "12": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
      },
      days: {
        "1": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "2": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "3": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "4": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "5": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "6": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "7": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
      },
      balance: {
        positive: {
          bg: {
            r: 0,
            g: 59,
            b: 255,
            a: 0.8,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        negative: {
          bg: {
            r: 255,
            g: 0,
            b: 6,
            a: 0.61,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
      },
      files: {
        json: {
          color: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
          bg: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
        },
        csv: {
          color: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
          bg: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
        },
        xlsx: {
          color: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
          bg: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
        },
        pdf: {
          color: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
          bg: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
        },
        png: {
          color: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
          bg: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
        },
        jpg: {
          color: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
          bg: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
        },
        jpeg: {
          color: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
          bg: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
        },
        xml: {
          color: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
          bg: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
        },
      },
      statuses: {
        pending: {
          color: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
          bg: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
        },
        null: {
          color: {
            r: 250,
            g: 15,
            b: 54,
            a: 1,
          },
          bg: {
            r: 75,
            g: 85,
            b: 99,
            a: 1,
          },
        },
        paid: {
          color: {
            r: 74,
            g: 222,
            b: 128,
            a: 1,
          },
          bg: {
            r: 41,
            g: 224,
            b: 15,
            a: 1,
          },
        },
      },
    }),
    [],
  );

  const userColors = user?.data?.data?.data?.settings?.colors || {};

  const mergedColors = useMemo(
    () => merge({}, defaultColors, userColors),
    [defaultColors, userColors],
  );

  const allPaymentMethods = useMemo(() => {
    const builtIn = PAYMENT_METHODS();
    const custom = user?.data?.data?.data?.settings?.customPaymentMethods || [];

    if (has(custom, "default")) {
      return sortBy([...builtIn], ["name"]);
    }

    return sortBy([...builtIn, ...custom], ["name"]);
  }, [user?.data?.data?.data?.settings?.customPaymentMethods]);

  const getPaymentMethodById = (paymentMethodId: string) => {
    return allPaymentMethods?.find?.(({ id }) => id === paymentMethodId);
  };

  const allEnabledPaymentMethods = useMemo(() => {
    const builtIn =
      user?.data?.data?.data?.settings?.payment_methods?.map?.((id: string) => {
        const data = getPaymentMethodById(id);

        return data;
      }) || [];
    const custom =
      user?.data?.data?.data?.settings?.customPaymentMethods?.filter?.(
        (paymentMethod: any) => paymentMethod?.enabled,
      ) || [];

    return sortBy([...builtIn, ...custom], ["name"]);
  }, [
    user?.data?.data?.data?.settings?.payment_methods,
    user?.data?.data?.data?.settings?.customPaymentMethods,
    allPaymentMethods,
  ]);

  const allBanks = useMemo(() => {
    return _.chain(accounts?.data?.data ?? [])
      .map("bank")
      .filter((b) => _.isString(b) && !_.isEmpty(b.trim()))
      .map((b) => b.trim())
      .uniq()
      .sortBy((b) => b.toLowerCase())
      .value();
  }, [accounts?.data?.data]);

  const hasInactiveAccounts = useMemo(() => {
    const allTheAccounts = accounts?.data?.data || [];
    if (!enabledAccountsList || !allTheAccounts) return false;

    return enabledAccountsList.length !== allTheAccounts.length;
  }, [enabledAccountsList, accounts?.data?.data]);

  const notificationsButtonIsVisible = useMemo(() => {
    return recurringTransactionsIsEnabled && myNetworkIsEnabled;
  }, [recurringTransactionsIsEnabled, myNetworkIsEnabled]);

  return {
    id: currentUserId,
    userInfo: user?.data?.data?.data,
    accounts,
    availableCurrencies,
    enabledPaymentMethods:
      user?.data?.data?.data?.settings?.payment_methods || [],
    colors: mergedColors,
    voiceAssistants: user?.data?.data?.data?.settings?.voiceAssistants || {
      alexa: { disabled: false },
      google: { disabled: false },
    },
    allCustomPaymentMethods:
      user?.data?.data?.data?.settings?.customPaymentMethods || [],
    getAccountById,
    resetCache,
    isLoadingAccounts: !!accounts?.isLoading,
    isRefetchingAccounts: !!accounts?.isRefetching,
    settings: user?.data?.data?.data?.settings || {},
    accountExist,
    hasRegisteredAccounts: !!accounts?.data?.data?.length,
    enabledAccountsList,
    allUserAccounts: accounts?.data?.data || [],
    allEnabledPaymentMethods,
    allPaymentMethods,
    getPaymentMethodById,
    allBanks,
    hasInactiveAccounts,
    notificationsButtonIsVisible,
  };
}
